import mqtt, { type MqttClient } from 'mqtt';

export interface ServoStatus {
  rgb1_angle: number;
  rgb2_angle: number;
  online: boolean;
}

export interface DeviceStatus {
  online: boolean;
  temperature: number;
  humidity: number;
}

type ServoCallback = (status: ServoStatus) => void;
type DeviceCallback = (status: DeviceStatus) => void;

const WS_URL = 'ws://mulgo.eemo.co.kr:9001/mqtt';
const USERNAME = 'solo';
const PASSWORD = 'solo^^!';

class MqttService {
  private client: MqttClient | null = null;
  private mac = '';
  private servoCallbacks: ServoCallback[] = [];
  private deviceCallbacks: DeviceCallback[] = [];
  private connectPromise: Promise<void> | null = null;
  private intentionalDisconnect = false;

  connect(mac: string): Promise<void> {
    if (this.client && this.client.connected && this.mac === mac) {
      return Promise.resolve();
    }

    this.mac = mac;
    this.intentionalDisconnect = false;

    if (this.connectPromise) {
      return this.connectPromise;
    }

    this.connectPromise = new Promise((resolve, reject) => {
      this.client = mqtt.connect(WS_URL, {
        username: USERNAME,
        password: PASSWORD,
        clientId: `web_${mac}_${Date.now()}`,
        clean: true,
        reconnectPeriod: 5000,
        connectTimeout: 10000,
      });

      this.client.on('connect', () => {
        if (!this.client) return;
        this.client.subscribe(`crop/${mac}/servo/status`, { qos: 0 });
        this.client.subscribe(`crop/${mac}/status`, { qos: 0 });
        this.connectPromise = null;
        resolve();
      });

      this.client.on('message', (topic: string, message: Uint8Array) => {
        try {
          const decoder = new TextDecoder();
          const data = JSON.parse(decoder.decode(message));
          if (topic === `crop/${this.mac}/servo/status`) {
            const status: ServoStatus = {
              rgb1_angle: data.rgb1_angle ?? 90,
              rgb2_angle: data.rgb2_angle ?? 90,
              online: data.online ?? false,
            };
            this.servoCallbacks.forEach((cb) => cb(status));
          } else if (topic === `crop/${this.mac}/status`) {
            const status: DeviceStatus = {
              online: data.online ?? false,
              temperature: data.temperature ?? 0,
              humidity: data.humidity ?? 0,
            };
            this.deviceCallbacks.forEach((cb) => cb(status));
          }
        } catch {
          // ignore parse errors
        }
      });

      this.client.on('error', (err: Error) => {
        this.connectPromise = null;
        if (!this.intentionalDisconnect) {
          reject(err);
        }
      });

      this.client.on('offline', () => {
        const offlineStatus: DeviceStatus = {
          online: false,
          temperature: 0,
          humidity: 0,
        };
        this.deviceCallbacks.forEach((cb) => cb(offlineStatus));
      });
    });

    return this.connectPromise;
  }

  sendServoCommand(camera: 'rgb1' | 'rgb2', angle: number): void {
    if (!this.client || !this.client.connected) return;
    const clamped = Math.round(Math.max(60, Math.min(120, angle)));
    this.client.publish(
      `crop/${this.mac}/servo/command`,
      JSON.stringify({ camera, angle: clamped }),
      { qos: 0 },
    );
  }

  onServoStatus(cb: ServoCallback): () => void {
    this.servoCallbacks.push(cb);
    return () => {
      this.servoCallbacks = this.servoCallbacks.filter((c) => c !== cb);
    };
  }

  onDeviceStatus(cb: DeviceCallback): () => void {
    this.deviceCallbacks.push(cb);
    return () => {
      this.deviceCallbacks = this.deviceCallbacks.filter((c) => c !== cb);
    };
  }

  disconnect(): void {
    this.intentionalDisconnect = true;
    this.connectPromise = null;
    if (this.client) {
      this.client.end(true);
      this.client = null;
    }
    this.servoCallbacks = [];
    this.deviceCallbacks = [];
  }
}

export const mqttService = new MqttService();